import BlockIcon from "@mui/icons-material/BlockRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import RestoreIcon from "@mui/icons-material/RestoreRounded";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useConfirmationDialogContext } from "@shared";
import type { ParticipantFormType } from "@shared";
import { ParticipantFormFields } from "@shared";
import type { FormikProps } from "formik";

import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { formatTimestamp } from "@/common/utils/formatTimestamp";
import { useTranslation } from "@/translations";

import { useParticipantReactivate } from "./hooks/useParticipantReactivate";

interface Props {
  participantId: string;
  participantName: string;
  deactivatedAtUtc: Date | null;
  deactivationReason: string | null;
  deactivatedByUserCode: string | null;
  mergeHistory: string | null;
  formik: FormikProps<ParticipantFormType>;
  onClose: () => void;
}

export function InactiveParticipantViewDialog({
  participantId,
  participantName,
  deactivatedAtUtc,
  deactivationReason,
  deactivatedByUserCode,
  mergeHistory,
  formik,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const reactivateMutation = useParticipantReactivate(participantId, () => onClose());

  const handleReactivate = () => {
    showConfirmationDialog({
      dialogTitleText: t("proceedingParticipants.reactivate.dialogTitle"),
      dialogText: (
        <Stack spacing={2}>
          <Typography variant="body2">
            {t("proceedingParticipants.reactivate.dialogBody", { participantName })}
          </Typography>
          {deactivationReason && (
            <Typography variant="body2" color="text.secondary">
              {t("proceedingParticipants.reactivate.previousReason", { reason: deactivationReason })}
            </Typography>
          )}
        </Stack>
      ),
      agreeText: t("proceedingParticipants.reactivate.confirmButton"),
      agreeIcon: <RestoreIcon />,
      discardText: t("proceedingParticipants.reactivate.cancelButton"),
      onClose: (confirmed) => {
        if (confirmed) {
          reactivateMutation.mutate();
        }
      },
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
        variant="h1"
      >
        {t("proceedingParticipants.edit.viewDialogTitle")}
        <IconButton onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5 }}>
        <Stack spacing={2}>
          {deactivatedAtUtc && (
            <Alert severity="info" icon={<BlockIcon />} sx={{ whiteSpace: "pre-line" }}>
              {deactivatedByUserCode
                ? t("proceedingParticipants.edit.inactiveBanner", {
                    date: formatTimestamp(deactivatedAtUtc, t, language),
                    user: deactivatedByUserCode,
                  })
                : t("proceedingParticipants.edit.inactiveBannerNoUser", {
                    date: formatTimestamp(deactivatedAtUtc, t, language),
                  })}
            </Alert>
          )}
          {deactivationReason && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t("proceedingParticipants.edit.deactivationReasonLabel")}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {deactivationReason}
              </Typography>
            </Stack>
          )}
          {mergeHistory && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t("proceedingParticipants.merge.historyLabel")}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {mergeHistory}
              </Typography>
            </Stack>
          )}
          <ParticipantFormFields formik={formik} disabled />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ p: 5, pt: 0, gap: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleReactivate}
          disabled={reactivateMutation.isPending}
        >
          {t("proceedingParticipants.edit.reactivate")}
        </Button>
        <Button variant="contained" onClick={onClose}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
